import { createContext, useContext, ReactNode } from 'react';
import { BREAKPOINT_SMALL, BREAKPOINT_MEDIUM, BREAKPOINT_LARGE } from '../components/layout/styles';
import { useMediaQuery } from './useMediaQuery';

// Define the type for our breakpoint values
interface BreakpointValues {
  isSmall: boolean;
  isMedium: boolean;
  isLarge: boolean;
  smallerThanSmall: boolean;
  smallerThanMedium: boolean;
  smallerThanLarge: boolean;
  largerThanSmall: boolean;
  largerThanMedium: boolean;
  largerThanLarge: boolean;
}

// Create a context with default values
const BreakpointContext = createContext<BreakpointValues | undefined>(undefined);

/**
 * Provider component that wraps the application to provide breakpoint values
 */
export const BreakpointProvider = ({ children }: { children: ReactNode }) => {
  const breakpointValues = useBreakpointValues();

  return <BreakpointContext.Provider value={breakpointValues}>{children}</BreakpointContext.Provider>;
};

/**
 * Hook that returns the current breakpoint values
 */
export const useBreakpoint = (): BreakpointValues => {
  const context = useContext(BreakpointContext);

  if (context === undefined) {
    throw new Error('useBreakpoint must be used within a BreakpointProvider');
  }

  return context;
};

/**
 * Internal hook that calculates the breakpoint values
 */
const useBreakpointValues = (): BreakpointValues => {
  // Check if screen is smaller than breakpoints
  const smallerThanSmall = useMediaQuery(`(max-width: ${BREAKPOINT_SMALL}px)`);
  const smallerThanMedium = useMediaQuery(`(max-width: ${BREAKPOINT_MEDIUM}px)`);
  const smallerThanLarge = useMediaQuery(`(max-width: ${BREAKPOINT_LARGE}px)`);

  // Check if screen is larger than breakpoints
  const largerThanSmall = useMediaQuery(`(min-width: ${BREAKPOINT_SMALL + 1}px)`);
  const largerThanMedium = useMediaQuery(`(min-width: ${BREAKPOINT_MEDIUM + 1}px)`);
  const largerThanLarge = useMediaQuery(`(min-width: ${BREAKPOINT_LARGE + 1}px)`);

  // Specific breakpoint ranges
  const isSmall = smallerThanSmall;
  const isMedium = !smallerThanSmall && smallerThanMedium;
  const isLarge = !smallerThanMedium && smallerThanLarge;

  return {
    isSmall,
    isMedium,
    isLarge,
    smallerThanSmall,
    smallerThanMedium,
    smallerThanLarge,
    largerThanSmall,
    largerThanMedium,
    largerThanLarge,
  };
};
